import { Activity, ClipboardCheck, Crosshair, LayoutGrid, Mail, Radar, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Eyebrow } from "@/components/istituti/Eyebrow";
import { FeatureCard } from "@/components/aziende/FeatureCard";
import { INSIGHTS_MAILTO } from "@/components/aziende/plans";

const TOOLS = [
  { id: "test", Icon: ClipboardCheck, title: "Test attitudinale dei dipendenti", text: "10 minuti dal telefono, link da condividere, nessun login. Ogni dipendente indica reparto e natura del ruolo; il profilo in azienda compare con un codice anonimo." },
  { id: "pulse", Icon: Activity, title: "Prodigy Pulse", text: "Una domanda al mese via email, risposta con un tocco (verde/giallo/rosso) e nota anonima facoltativa. Indice dell'umore per reparto e lettura di Argos che spiega l'andamento.", pulse: true },
  { id: "mismatch", Icon: Crosshair, title: "Mismatch Radar (Allineamento)", text: "Evidenzia dove le attitudini di una persona e la natura del suo ruolo non coincidono: uno spunto di ascolto, mai un giudizio." },
  { id: "scacchiera", Icon: LayoutGrid, title: "Scacchiera dell'Ecosistema", text: "Trascina le pedine anonime tra i reparti e vedi in tempo reale radar, equilibrio e spinte dominanti di ogni team. Salva scenari «e se…»; quando a un reparto manca un profilo, Argos lo segnala e ti porta nel Pool Candidati." },
  { id: "turnover", Icon: Radar, title: "Turnover Radar", text: "Alert anonimi di reparto quando mismatch e Pulse rossi consecutivi si sommano. Con il «Confronto riservato» puoi proporre un dialogo al dipendente, che resta libero di accettare o no." },
];

const PRIVACY = [
  "Profili visibili solo da 5 test completati per azienda",
  "Reparti con meno di 3 persone aggregati",
  "Identità visibile solo se il dipendente accetta esplicitamente la richiesta",
  "Report PDF per la direzione sempre anonimo",
];

const PulseDots = () => (
  <div className="mt-6 flex items-center gap-2" aria-hidden="true">
    {["#22c55e", "#facc15", "#ef4444"].map((c) => (
      <span key={c} className="h-3 w-3 rounded-full" style={{ background: c, boxShadow: `0 0 10px ${c}` }} />
    ))}
  </div>
);

const Anonimato = () => (
  <div className="glass rounded-[24px] border border-[#1e2024] p-6 sm:p-8" data-testid="insights-anonimato">
    <p className="flex items-center gap-3 font-bold text-lg text-white">
      <ShieldCheck className="h-5 w-5 text-cyan" strokeWidth={1.75} /> Anonimato by design
    </p>
    <ul className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {PRIVACY.map((p) => (
        <li key={p} className="flex gap-2.5 text-sm leading-relaxed text-titanium">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#00e5ff] shadow-[0_0_8px_#00e5ff]" />
          {p}
        </li>
      ))}
    </ul>
  </div>
);

const Packages = () => (
  <div className="bridge-panel glass rounded-[24px] border p-6 sm:p-8 flex flex-col lg:flex-row lg:items-center gap-6 lg:justify-between" data-testid="insights-packages">
    <div className="max-w-2xl">
      <p className="text-base sm:text-lg text-white/90 leading-relaxed">
        Incluso in Horizon (base), Vision e Omega (completo). Disponibile anche da solo con il pacchetto Insights, € 150/mese o € 1.800/anno, su richiesta.
      </p>
      <p className="mt-3 text-xs text-[#6b7078]">Versione Beta a solo scopo dimostrativo, per i primi partner che vogliono testarlo.</p>
    </div>
    <a href={INSIGHTS_MAILTO} className="cta cta-mega glass inline-flex min-h-[52px] shrink-0 items-center justify-center gap-2 rounded-full border px-8 py-4 text-sm sm:text-base font-bold text-white" data-testid="insights-cta">
      <Mail className="h-4 w-4 text-cyan" /> Richiedi l'attivazione
    </a>
  </div>
);

export const InsightsSection = () => (
  <section id="insights" className="relative px-6 py-20 lg:py-36 scroll-mt-24" data-testid="insights-section">
    <div className="max-w-7xl mx-auto">
      <Reveal className="mb-14 lg:mb-20">
        <div className="mb-6 flex items-center gap-3">
          <Eyebrow>Prodigy Insights</Eyebrow>
          <span className="beta-pill rounded-full border px-2.5 py-0.5 text-[10px] font-semibold tracking-[0.2em] uppercase" data-testid="insights-beta-badge">BETA</span>
        </div>
        <h2 className="max-w-4xl font-extrabold tracking-[-0.04em] leading-[1.02] text-4xl sm:text-5xl lg:text-6xl" data-testid="insights-title">
          Prodigy Insights — <span className="text-titanium">Ascolta la tua organizzazione</span>
        </h2>
        <p className="mt-6 max-w-3xl text-base sm:text-lg text-titanium leading-relaxed">
          Un test attitudinale anonimo per i tuoi dipendenti e cinque strumenti per leggere i segnali del clima aziendale prima che diventino dimissioni. Nessun nome, nessuna email: l'azienda vede solo dati anonimi e aggregati.
        </p>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
        {TOOLS.map(({ pulse, ...t }, i) => (
          <Reveal key={t.id} delay={(i % 3) * 110} className="h-full">
            <FeatureCard {...t} n={i + 1} testId={`insights-card-${t.id}`}>{pulse && <PulseDots />}</FeatureCard>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-6 space-y-6">
        <Anonimato />
        <Packages />
      </Reveal>
    </div>
  </section>
);
